import React from "react";
import { Search, Download } from "lucide-react";
import { Combobox } from "components/ui/combobox";
import { MultiSelectCombobox } from "components/ui/multi-select-combobox";

export interface Level {
    id: number | string;
    name: string;
    visible?: boolean;
    selected?: string[] | null;
    options?: Array<{ ou_code: string; ou_name: string }>;
    locked?: string[] | null;
}

interface Props {
    year: string;
    years?: string[];
    month: number;
    months: { value: number; label: string }[];
    day: number;
    days: number[];
    levels: Level[];
    loading: boolean;
    search: string;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
    onSearchChange: (val: string) => void;
    onYearChange?: (year: string) => void;
    onMonthChange?: (month: number) => void;
    onDayChange?: (day: number) => void;
    onSelectChange: (levelId: number | string, value: string[]) => void;
    onDownloadExcel: () => void;
    downloadingExcel?: boolean;
}

const LOCKED_MULTI_VALUE = "__locked_multiple__";

const UnbilledDropdowns: React.FC<Props> = ({
    year,
    years,
    month,
    months,
    day,
    days,
    levels,
    loading,
    search,
    conversionType,
    onConversionChange,
    onSearchChange,
    onYearChange,
    onMonthChange,
    onDayChange,
    onSelectChange,
    onDownloadExcel,
    downloadingExcel = false,
}) => {

    const visibleLevels = React.useMemo(
        () =>
            (levels || [])
                .filter((l) => l.visible)
                .sort((a, b) => Number(b.id) - Number(a.id)),
        [levels]
    );

    return (
        <div className="flex flex-col gap-4 w-full">
            <div
                className="grid gap-4 items-start w-full"
                style={{ gridTemplateColumns: "140px 130px 90px repeat(auto-fit, minmax(140px, 1fr))" }}
            >
                <div className="min-w-0">
                    <label className="block mb-1 text-sm font-medium text-gray-700">Year</label>
                    <Combobox
                        className="h-10 text-sm"
                        options={(years || []).map((y) => ({
                            value: y,
                            label: y,
                        }))}
                        value={year}
                        onValueChange={(val) => val && onYearChange?.(val)}
                        disabled={loading}
                    />
                </div>

                <div className="min-w-0">
                    <label className="block mb-1 text-sm font-medium text-gray-700">Month</label>
                    <Combobox
                        className="h-10 text-sm"
                        options={months.map((m) => ({
                            value: String(m.value),
                            label: m.label,
                        }))}
                        value={String(month)}
                        onValueChange={(val) =>
                            val && onMonthChange?.(Number(val))
                        }
                        disabled={loading}
                    />
                </div>

                <div className="min-w-0">
                    <label className="block mb-1 text-sm font-medium text-gray-700">Day</label>
                    <Combobox
                        className="h-10 text-sm"
                        options={days.map((d) => ({
                            value: String(d),
                            label: String(d),
                        }))}
                        value={String(day)}
                        onValueChange={(val) =>
                            val && onDayChange?.(Number(val))
                        }
                        disabled={loading}
                    />
                </div>

                {visibleLevels.map((level) => {
                    const baseOpts =
                        level.options?.map((o) => ({
                            value: o.ou_code,
                            label: o.ou_name,
                        })) || [];

                    const lockedCodes = Array.isArray(level.locked) ? level.locked : [];
                    const isLockedLevel = lockedCodes.length > 0;
                    const hasMultipleLocked = lockedCodes.length > 1;

                    const opts =
                        Number(level.id) !== 1 && isLockedLevel && hasMultipleLocked
                            ? [
                                  ...baseOpts,
                                  {
                                      value: LOCKED_MULTI_VALUE,
                                      label: `${lockedCodes.length} selected`,
                                  },
                              ]
                            : baseOpts;

                    return (
                        <div key={String(level.id)} className="min-w-0">
                            <label className="block text-sm font-medium mb-1 text-gray-700">
                                {level.name}
                            </label>

                            {Number(level.id) === 1 ? (
                                <MultiSelectCombobox
                                    options={baseOpts}
                                    value={isLockedLevel ? lockedCodes : Array.isArray(level.selected) ? level.selected : []}
                                    disabled={loading || isLockedLevel}
                                    onValueChange={(val) => {
                                        if (isLockedLevel) return;
                                        onSelectChange(level.id, val);
                                    }}
                                    searchPlaceholder={`Search ${level.name.toLowerCase()}...`}
                                    emptyText={`No ${level.name.toLowerCase()} found.`}
                                    buttonClassName="h-10 px-2.5 text-sm"
                                />
                            ) : (
                                <Combobox
                                    className="h-10 text-sm"
                                    options={opts}
                                    value={isLockedLevel ? hasMultipleLocked ? LOCKED_MULTI_VALUE : lockedCodes[0] ?? "" : Array.isArray(level.selected) && level.selected.length > 0 ? level.selected[0] : ""}
                                    disabled={loading || isLockedLevel}
                                    onValueChange={(val) => {
                                        if (isLockedLevel) return;
                                        onSelectChange(level.id, val ? [val] : []);
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="flex items-end justify-between w-full">
                <div className="min-w-0 w-[280px]">
                    <label className="block text-sm font-medium mb-1 text-gray-700">Search Customer</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search by customer name..."
                            className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                    </div>
                </div>

                <div className="flex items-end gap-2 shrink-0 pb-[2px]">
                    {(['INR', 'Lakhs', 'Crores'] as const).map((t) => (
                        <button
                            key={t}
                            onClick={() => onConversionChange(t)}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${conversionType === t ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                        >
                            {t}
                        </button>
                    ))}
                    
                    <button
                        className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onDownloadExcel}
                        disabled={downloadingExcel}
                    >
                        <Download size={16} />
                        {downloadingExcel ? 'Downloading...' : 'Excel'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnbilledDropdowns;